import { produce } from 'immer'
import React from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import DatePicker, { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'
import '../../../../assets/css/style.css'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

// 日本語ロケールを登録
registerLocale('ja', ja)

/**
 * @function ShowErrors
 * @param {Object} props
 * @param {Record<string, string[]>} props.errors
 * @param {string} props.field
 */
export const ShowErrors = ({ errors: errorMessage, field = '' }) => {
  if (!errorMessage?.[field]) return <></>

  return (
    <>
      {errorMessage[field].map((err, index) => (
        <p key={index} className="text-red-500 text-sm">
          {err}
        </p>
      ))}
    </>
  )
}

const RequiredField = (props) => <span className={`red--text ${props?.className ?? ''}`}>*</span>

/**
 * @function FormInput
 * @param {FormInputProps} props
 */
const FormInput = ({ id, label, required = true, placeholder, value, errors, disabled, onChange }) => {
  const isError = errors?.[id]?.length > 0

  return (
    <tr>
      <th className="form-table__col-head w-[150px]">
        <label htmlFor={id} className="text-h7 font-bold">
          {label} {required && <RequiredField />}
        </label>
      </th>
      <td className="form-table__col-data form-control">
        <input
          id={id}
          name={id}
          className={`text-input w-full text-left ${isError && 'is-error'} min-h-[46px]`}
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
        />
        <ShowErrors field={id} errors={errors} />
      </td>
    </tr>
  )
}

/**
 * @typedef {Object} GroupCompanyInfoActions
 * @property {function} setErrors
 * @property {function} resetErrors
 * @property {function} updateFormValues
 */

/**
 * @function GroupCompanyInfoModal
 * @type {React.FC<{
 *  editMode:boolean,
 *   defaultValues: GroupCompanyInfo
 *   onChange?: function(GroupCompanyInfo , GroupCompanyInfoActions):void
 * }>}
 */
export const GroupCompanyInfoModal = (props) => {
  const { defaultValues, onChange, editMode = false } = props

  const initialErrors = {
    corporationNumber: [],
    compName: [],
    compType: [],
    validStartDate: [],
    validEndDate: [],
    memo: []
  }

  const [errors, setErrors] = React.useState(initialErrors)

  const initValues = {
    key: '',
    corporationNumber: '',
    compName: '',
    compType: '',
    validStartDate: null,
    validEndDate: null,
    memo: ''
  }

  const [formData, setFormValues] = React.useState(initValues)
  const { corporationNumber, compName, compType, validStartDate, validEndDate, memo } = formData

  /**
   * @function updateFormValues フォームの値を更新
   * @param {string} field
   * @returns {function (ChangeEvent):void}
   */
  const updateFormValues = (field) => (e) => {
    setFormValues(
      produce((draft) => {
        draft[field] = e?.target ? e.target.value : e
      })
    )
  }

  /** @type {{ label: string; value: string }} 企業種類 */
  const compTypeOptions = React.useMemo(
    () =>
      ['提出会社', '子会社', '特定子会社', '関連会社', '持分法適用会社', 'その他子会社'].map((e) => ({
        label: e,
        value: e
      })),
    []
  )

  const resetErrors = () => {
    setErrors(initialErrors)
  }

  React.useEffect(() => {
    if (!defaultValues) return

    setFormValues(defaultValues)
  }, [defaultValues])

  React.useEffect(() => {
    const formActions = { updateFormValues, setErrors, resetErrors }
    onChange?.(formData, formActions)
  }, [formData])

  return (
    <>
      <div className=" is-benchmark">
        <table className="form-table table-fixed w-full">
          <tbody>
            {/** ================================= 法人番号 ======================================== */}
            {!editMode && (
              <FormInput
                id="corporationNumber"
                label={'法人番号'}
                value={corporationNumber}
                onChange={updateFormValues('corporationNumber')}
                required
                placeholder="法人番号半角数字を入力してください"
                errors={errors}
              />
            )}
            {editMode && (
              <tr>
                <th className="form-table__col-head w-[150px]">
                  <label htmlFor="corporationNumber" className="text-h7 font-bold">
                    法人番号 <RequiredField />
                  </label>
                </th>
                <td className="form-table__col-data">
                  <span className="text-sm block">{corporationNumber}</span>
                  <ShowErrors field={'corporationNumber'} errors={errors} />
                </td>
              </tr>
            )}

            {/** ================================= 法人番号 ======================================== */}

            {/** ================================= 会社名 ======================================== */}
            <FormInput
              id="compName"
              label={'会社名'}
              required
              value={compName}
              placeholder="会社名を入力してください"
              onChange={updateFormValues('compName')}
              errors={errors}
            />
            {/** ================================= 会社名 ======================================== */}

            {/** ================================= 会社種類 ======================================== */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor={'compType'} className="text-h7 font-bold">
                  会社種類
                </label>
              </th>
              <td className="form-table__col-data form-control">
                <ChrofyCustomSelect
                  id="compType"
                  value={compType}
                  isError={errors?.['compType']?.length > 0}
                  options={compTypeOptions}
                  onChange={updateFormValues('compType')}
                />

                <ShowErrors field={'compType'} errors={errors} />
              </td>
            </tr>
            {/** ================================= 会社種類 ======================================== */}

            {/** ================================= 有効期間 ======================================== */}

            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">有効期間</label>
              </th>
              <td className="form-table__col-data">
                <div className="flex gap-5 items-center form-control ">
                  <DatePicker
                    id="validStartDate"
                    name="validStartDate"
                    selected={validStartDate}
                    onChange={updateFormValues('validStartDate')}
                    showMonthYearPicker
                    dateFormat="yyyy/MM"
                    className={`rounded-lg w-[150px] min-h-[46px] text-input text-left ${errors?.validStartDate?.length > 0 ? 'is-error' : ''}`}
                    placeholderText="YYYY/MM"
                    autoComplete="off"
                    locale="ja"
                    portal
                  />

                  <div className="relative -ml-[10px]">
                    <RequiredField />
                  </div>
                  <span>～</span>
                  <DatePicker
                    id="validEndDate"
                    name="validEndDate"
                    selected={validEndDate}
                    onChange={updateFormValues('validEndDate')}
                    showMonthYearPicker
                    dateFormat="yyyy/MM"
                    className={`rounded-lg w-[150px] min-h-[46px] text-input text-left ${errors?.validEndDate?.length > 0 ? 'is-error' : ''}`}
                    placeholderText="YYYY/MM"
                    autoComplete="off"
                    locale="ja"
                    minDate={validStartDate || null}
                    popperPlacement="bottom-start"
                    portalId="root-portal"
                    popperContainer={({ children }) => (
                      <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 1500, minWidth: 280 }}>
                        {children}
                      </div>
                    )}
                  />
                </div>

                <ShowErrors field={'validStartDate'} errors={errors} />
                <ShowErrors field={'validEndDate'} errors={errors} />
              </td>
            </tr>
            {/** ================================= 有効期間 ======================================== */}

            {/* =======================メモ====================== */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="memo" className="text-h7 font-bold">
                  メモ
                </label>
              </th>
              <td className="form-table__col-data form-control">
                <textarea
                  className="modal-window__content-textarea rounded-lg px-4 py-3 w-full h-auto min-h-[120px] text-sm"
                  placeholder="メモを入力してください（全角200文字程度）"
                  maxLength={200}
                  id="memo"
                  name="memo"
                  value={memo}
                  onChange={updateFormValues('memo')}
                ></textarea>

                <ShowErrors field={'memo'} errors={errors} />
              </td>
            </tr>
            {/* =======================メモ====================== */}
          </tbody>
        </table>
      </div>
    </>
  )
}
